import { PencilIcon, type LucideIcon } from 'lucide-react';
import { Fragment, useId } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import { Button } from '@/components/core/button';
import { Skeleton } from '@/components/core/skeleton';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { cn } from '@/shared/helpers';

export type DetailListItem = {
  label: ReactNode;
  value?: ReactNode;
  icon?: LucideIcon;
  key?: string;
};

export type DetailListProps = {
  title?: ReactNode;
  items: DetailListItem[];
  isLoading?: boolean;
  action?: ReactNode;
  className?: string;
};

export function DetailEditButton({
  children = 'Edit',
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant'>) {
  return (
    <Button
      type="button"
      variant="ghost"
      className={cn('-mr-1.5 h-7 px-1.5', className)}
      {...props}
    >
      <PencilIcon className="size-3.5" />
      {children}
    </Button>
  );
}

/**
 * Renders a labelled key/value list. When wrapped in a `<DetailGroup>`, sibling
 * lists share a label column (via subgrid) and gain dividers between sections.
 */
export function DetailList({ title, items, isLoading, action, className }: DetailListProps) {
  const titleId = useId();

  return (
    <section
      data-slot="detail-list"
      aria-labelledby={title ? titleId : undefined}
      aria-busy={isLoading}
      className={cn(
        'space-y-2',
        // When nested in a <DetailGroup>, opt into the parent's subgrid
        'in-data-[slot=detail-group]:col-span-full in-data-[slot=detail-group]:grid in-data-[slot=detail-group]:grid-cols-subgrid in-data-[slot=detail-group]:space-y-0',
        // and add dividers/padding between sibling sections.
        'in-data-[slot=detail-group]:border-t in-data-[slot=detail-group]:py-5 in-data-[slot=detail-group]:first:border-t-0 in-data-[slot=detail-group]:first:pt-0 in-data-[slot=detail-group]:last:pb-0',
        className,
      )}
    >
      {title || action ? (
        <div
          className={cn(
            'flex items-center justify-between gap-2',
            'in-data-[slot=detail-group]:col-span-full in-data-[slot=detail-group]:mb-3',
          )}
        >
          {title ? (
            <div id={titleId} className="font-heading text-sm leading-snug font-semibold">
              {title}
            </div>
          ) : (
            <span />
          )}
          {action}
        </div>
      ) : null}
      <dl
        className={cn(
          'grid max-w-full grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm',
          'in-data-[slot=detail-group]:col-span-full in-data-[slot=detail-group]:grid-cols-subgrid',
        )}
      >
        {items.map((item, index) => {
          const Icon = item.icon;

          return (
            <Fragment
              key={item.key ?? (typeof item.label === 'string' ? item.label : `detail-${index}`)}
            >
              <dt className="flex min-w-0 items-start gap-2 font-medium text-muted-foreground">
                {Icon ? <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden /> : null}
                <span className="min-w-0 wrap-break-word">{item.label}</span>
              </dt>
              <dd className="min-w-0 font-medium wrap-break-word">
                {isLoading ? (
                  <Skeleton className="h-4 w-full" aria-label="Loading value" />
                ) : (
                  (item.value ?? <span className="text-muted-foreground">{EMPTY_TEXT_VALUE}</span>)
                )}
              </dd>
            </Fragment>
          );
        })}
      </dl>
    </section>
  );
}
